import * as React from 'react';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'components/ui/sheet';

/**
 * La hoja inferior (bottom sheet) de las barras móviles: la de planes, la de empresas, la de cuenta y la
 * de «Más». Una sola pieza para que las cuatro se vean y se comporten igual, con lo que se espera de una
 * hoja inferior:
 *
 * - **Asa** arriba, la rayita gris centrada (36 por 4 px) que dice «esto se puede arrastrar». Y se puede:
 *   arrastrando hacia abajo desde el asa o el título (más de 96 px) se cierra. Además se cierra con el
 *   fondo, con Escape y con la X, para quien no arrastra (teclado, lector de pantalla).
 * - **Título visible**, no oculto: dice qué es la hoja («Tus planes»). También es su nombre accesible.
 * - **Espaciado** de la rejilla de 8 px: 8 de aire sobre el asa, 16 a los lados del título, filas de 48 px
 *   de alto como mínimo (área táctil cómoda) con 4 entre ellas, y 16 abajo MÁS el área segura del
 *   indicador de inicio, para que la última fila no quede pegada al borde.
 * - **Alto máximo** del 85 % de la pantalla (de su contenedor fijo: el viewport, o el marco en Storybook), con el contenido desplazable: una lista larga no tapa la
 *   pantalla entera ni empuja el título fuera.
 *
 * `container` es el marco donde se monta (en Storybook, el marco de 390 px): ver `MobileFrameContext`.
 */
export interface NavSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	children: React.ReactNode;
	container?: HTMLElement;
}

/** Lo que hay que arrastrar hacia abajo, en píxeles, para cerrar la hoja. */
const UMBRAL_DE_CIERRE = 96;

export const NavSheet = ({ open, onOpenChange, title, children, container }: NavSheetProps) => {
	const inicio = React.useRef<number | null>(null);
	const [desplazamiento, setDesplazamiento] = React.useState(0);

	const empezar = (event: React.PointerEvent<HTMLDivElement>) => {
		// Solo el botón principal (o el dedo): un clic derecho no arrastra.
		if (event.pointerType === 'mouse' && event.button !== 0) return;
		inicio.current = event.clientY;
		event.currentTarget.setPointerCapture(event.pointerId);
	};
	const mover = (event: React.PointerEvent<HTMLDivElement>) => {
		if (inicio.current === null) return;
		// Solo hacia abajo: hacia arriba la hoja no sube más.
		setDesplazamiento(Math.max(0, event.clientY - inicio.current));
	};
	const terminar = () => {
		if (inicio.current === null) return;
		const cierra = desplazamiento >= UMBRAL_DE_CIERRE;
		inicio.current = null;
		setDesplazamiento(0);
		if (cierra) onOpenChange(false);
	};

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="bottom"
				container={container}
				// p-0 y gap-0: el relleno lo ponen las piezas de dentro, para que el asa y el área de
				// arrastre lleguen a los bordes. `pb-0` anula el relleno de área segura de la base (que
				// se suma abajo, en el contenido).
				className="flex max-h-[85%] flex-col gap-0 rounded-t-[20px] p-0 pb-0"
				style={
					desplazamiento > 0
						? { transform: `translateY(${desplazamiento}px)`, transition: 'none' }
						: undefined
				}
			>
				{/* Zona de arrastre: el asa y el título. */}
				<div
					className="shrink-0 cursor-grab touch-none select-none"
					onPointerDown={empezar}
					onPointerMove={mover}
					onPointerUp={terminar}
					onPointerCancel={terminar}
				>
					<div
						aria-hidden="true"
						className="mx-auto mt-2 h-1 w-9 rounded-full bg-gray-300 dark:bg-muted-foreground/40"
					/>
					<SheetHeader className="px-4 pb-3 pt-3 text-left">
						<SheetTitle className="text-base font-semibold">{title}</SheetTitle>
					</SheetHeader>
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto px-2 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
					{children}
				</div>
			</SheetContent>
		</Sheet>
	);
};
